import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import SearchIcon from '@material-ui/icons/Search';
import InputBase from '@material-ui/core/InputBase';
import {
  getAssessmentExaminerStructure,
  getSampleAssessmentExaminerStructure,
} from '../../services/exams/assessmentSession/assessments.service';
import { BlockadeTypeId, ExaminersList, UserIds } from '../ExaminersStructure/ExaminersStructure.types';
import { useStyles } from './SidebarList.styles';
import { useExaminer } from '../../Context/Examiner.context';
import { useParams } from 'react-router-dom';
import TooltipOne from '../../components/TooltipOne/TooltipOne.component';
import { getSampleAssessmentUrl } from '../../utils/routerUrls';
import { useHistory } from 'react-router-dom';
import useSessionUser from '../../hooks/useSessionUser';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { SignalRContext } from '../../Context/SignalR.context';
import { BlockadeMessage, CallbackMethodNames } from '../../models/signalR.types';
import { UserSigningInfoMessage } from '../../models/communication.types';
import { useLayoutContext } from '../../Context/Layout.context';
import DropdownMenuFilter from '../DropdownMenu/DropdownMenuFilter.component';
import FilterListIcon from '@material-ui/icons/FilterList';
import { ExaminersTree } from '../ExaminersStructure/ExaminersTree.component';
import {
  findStructureElement,
  getBlockadeFilter,
  getIdsFromAStructure,
  isSearchResult,
  updateExaminerStructureActiveExaminer,
  updateExaminerStructureClosedExaminer,
  updateExaminerStructureWithSignalRBlockadeInfo,
  updateExaminerStructureWithSignalRInfo,
} from './helpers';
import CircularProgress from '@material-ui/core/CircularProgress';
import { areObjectsEqual } from '../../utils/areObjectsEqual';
import { filtersActions } from '../../reducers/filtersReducer/filtersSlice';
import {
  DataInfoType,
  ExaminersStructureDataInfo,
  SampleExaminersStructureDataInfo,
} from '../../reducers/filtersReducer/filterSlice.types';

export enum assessmentTypes {
  assessment,
  sampleAssassment,
}

interface ISidebarList {
  isMinimized?: boolean;
  setIsMinimized?: (value: boolean) => void;
  assessmentType?: assessmentTypes;
}

const SidebarList: React.FC<ISidebarList> = ({ isMinimized, setIsMinimized, assessmentType }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  
  const dataInfoType = assessmentType === assessmentTypes.sampleAssassment
    ? DataInfoType.SampleExaminersStructureInfo
    : DataInfoType.ExaminersStructureInfo;
  
  const examinersStructureInfo = useSelector(
    (state: RootState) => state.filters[dataInfoType]
  ) as ExaminersStructureDataInfo | SampleExaminersStructureDataInfo;
  
  const searchPhrase = examinersStructureInfo?.filterData?.searchPhrase || '';
  
  const blockadeFilter = useMemo(() => {
    const fd = examinersStructureInfo?.filterData;
    if (!fd || typeof fd !== 'object') return [];
    return 'blockadeFilter' in fd ? (fd.blockadeFilter || []) : [];
  }, [examinersStructureInfo]);
  
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(true);
  const [examinerStructure, setExaminerStructure] = useState<ExaminersList | undefined>(undefined);
  const [displayedStructure, setDisplayedStructure] = useState<ExaminersList | null>(null);
  const [blockadeFilterType, setBlockadeFilterType] = useState<BlockadeTypeId>(BlockadeTypeId.None);
  const [anchorElBlockade, setAnchorElBlockade] = useState<SVGSVGElement | HTMLElement | null>(null);
  const { userAssessmentSessionInfo, user, isUserKK, isUserKGZ, isUserPZE, isSessionLoaded } = useSessionUser();

  const isUserAccessTokenProvided = !!user?.access_token;
  const isAssessmentSessionLoaded = !!userAssessmentSessionInfo;
  const showBlockadeFilter = (isUserKK || isUserKGZ || isUserPZE) && assessmentType === assessmentTypes.assessment;
  const { highContrastMode } = useLayoutContext();

  const classes = useStyles();
  const { setExaminer, setTeam, forceReloadExaminersTreemap, setForceReloadExaminersTreemap } = useExaminer();
  const [isEmptySearch, setIsEmptySearch] = useState<boolean>(false);
  const { examinerId } = useParams() as {
    examinerId: string;
  };

  SignalRContext.useSignalREffect(
    CallbackMethodNames.UserSigningInfo,
    (message: UserSigningInfoMessage) => {
      setExaminerStructure((prevState) => {
        if (!prevState) {
          return prevState;
        }
        const newExaminerStructure = updateExaminerStructureWithSignalRInfo(
          prevState,
          message.userId,
          message.signedIn,
        );
        return newExaminerStructure;
      });
    },
    [displayedStructure],
  );

  const signalRBlockadeCallback = (message: BlockadeMessage) => {
    setExaminerStructure((prevState) => {
      if (!prevState) {
        return prevState;
      }

      return updateExaminerStructureWithSignalRBlockadeInfo(
        prevState,
        message.examinerPermissionId,
        message.blockadeTypeId,
        message.assessmentSessionId,
        assessmentSession,
      );
    });
  };

  SignalRContext.useSignalREffect(CallbackMethodNames.ExaminerUnblocked, signalRBlockadeCallback, [assessmentSession]);
  SignalRContext.useSignalREffect(CallbackMethodNames.ExaminerBlocked, signalRBlockadeCallback, [assessmentSession]);

  const updateExaminerStructureDefaultExaminer = useCallback((data: ExaminersList) => {
    const defaultExaminer =
      (examinerId && findStructureElement(examinerId, data)) ||
      (user && user.profile && findStructureElement(user.profile.sub, data));
    if (defaultExaminer) {
      const newStructure = updateExaminerStructureActiveExaminer(data, defaultExaminer);
      setExaminer(defaultExaminer);
      return newStructure;
    }
    return data;
  }, [examinerId, user, setExaminer]);

  const refresh = useCallback(() => {
    if (isUserAccessTokenProvided && assessmentSession) {
      let ExaminersList;
      setForceReloadExaminersTreemap(false);
      switch (assessmentType) {
        case assessmentTypes.sampleAssassment:
          ExaminersList = getSampleAssessmentExaminerStructure().then(({ data }) => {
            return data;
          });
          break;
        case assessmentTypes.assessment:
          ExaminersList = getAssessmentExaminerStructure(assessmentSession.assessmentSessionId).then(({ data }) => {
            return data;
          });
          break;
      }

      if (ExaminersList) {
        ExaminersList.then((data: ExaminersList) => {
          setExaminerStructure(updateExaminerStructureDefaultExaminer(data));
          setTeam(data);
          if (data.inferiors.length === 1 && data.inferiors[0].inferiors.length === 0) {
            setIsSearchOpen(false);
          }
        }).catch(() => console.log('error'));
      }
    }
  }, [assessmentSession, assessmentType, setForceReloadExaminersTreemap, setTeam, isUserAccessTokenProvided, updateExaminerStructureDefaultExaminer]);

  useEffect(() => {
    refresh();
  }, [assessmentSession, user.profile.sub, forceReloadExaminersTreemap, refresh]);

  useEffect(() => {
    setSearchFilter(searchPhrase);
  }, [searchPhrase]);

  const filterStructure = useCallback(
    (element?: ExaminersList | null, search?: string): ExaminersList | null => {
      if (!element) return null;
      const searchText = search ?? '';
      const matchesBlockade = (() => {
        try {
          return getBlockadeFilter(blockadeFilterType, element);
        } catch {
          return false;
        }
      })();

      const matchesSearch = (() => {
        try {
          return isSearchResult(element, searchText);
        } catch {
          return false;
        }
      })();

      if (matchesBlockade && matchesSearch) {
        const newParams = {
          isHighlightText: true,
        };
        const needsToBeRecreated = !areObjectsEqual(element, newParams);

        if (needsToBeRecreated)
          return {
            ...element,
            ...newParams,
            inferiors: [],
          };
        return element;
      } else if (element.inferiors && element.inferiors.length) {
        let inferiorsAreRecreated = false;
        const newInferiors = element.inferiors
          .map((item) => {
            const newItem = filterStructure(item, searchText);
            if (newItem !== item) {
              inferiorsAreRecreated = true;
            }
            return newItem;
          })
          .filter((item) => !!item) as ExaminersList[];
        if (newInferiors.length) {
          if (!inferiorsAreRecreated) {
            return element;
          }
          return {
            ...element,
            inferiors: newInferiors,
          };
        }
      }
      return null;
    },
    [blockadeFilterType],
  );

  const startFiltering = useCallback(
    _.debounce((value: string) => {
      setSearchFilter(value);
      if (examinerStructure) {
        if (value.length || blockadeFilterType !== BlockadeTypeId.None) {
          const examiners = filterStructure(examinerStructure, value);
          setIsEmptySearch(examiners === null);
          setDisplayedStructure(examiners);
        } else {
          setIsEmptySearch(false);
          setDisplayedStructure(examinerStructure);
        }
      }
    }, 500),
    [blockadeFilterType, examinerStructure, filterStructure],
  );

  useEffect(() => {
    startFiltering(searchFilter);
  }, [searchFilter, examinerStructure, blockadeFilterType, filterStructure, startFiltering]);

  const updateActiveElement = (element: ExaminersList) => {
    setExaminerStructure((prevState) => {
      if (!prevState) {
        return prevState;
      }
      const newStructure = updateExaminerStructureActiveExaminer(prevState, element);
      return newStructure;
    });
  };

  const handleActiveElementChange = useCallback(
    (element: ExaminersList) => {
      updateActiveElement(element);
      setExaminer(element);
      if (assessmentType === assessmentTypes.sampleAssassment) {
        if (
          assessmentType === assessmentTypes.sampleAssassment &&
          assessmentSession &&
          assessmentSession.assessmentSessionId
        ) {
          history.push(getSampleAssessmentUrl(assessmentSession.assessmentSessionId, element.id));
        }
      }
    },
    [setExaminer, assessmentType, assessmentSession, history],
  );

  const updateClosedElement = useCallback((element?: ExaminersList, isClosed?: boolean) => {
    setExaminerStructure((prevState) => {
      if (!prevState) {
        return prevState;
      }
      const newStructure = updateExaminerStructureClosedExaminer(prevState, element, isClosed);
      return { ...newStructure };
    });
  }, []);

  const handleClosedElementChange = useCallback(
    (element: ExaminersList, isClosed?: boolean) => {
      updateClosedElement(element, isClosed);
    },
    [updateClosedElement],
  );

  const onReload = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleStructureClick = () => {
    if (isMinimized && setIsMinimized) {
      setIsMinimized(false);
    }
  };

  const handleClickBlockadeMenu = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!anchorElBlockade) {
      setAnchorElBlockade(event.currentTarget);
    } else setAnchorElBlockade(null);
  };

  useEffect(() => {
    if (blockadeFilter.length) {
      if (blockadeFilter.some((e) => e === 'Automatyczna')) setBlockadeFilterType(BlockadeTypeId.Automatic);
      if (blockadeFilter.some((e) => e === 'Ręczna')) setBlockadeFilterType(BlockadeTypeId.Manual);
      if (blockadeFilter.some((e) => e === 'Automatyczna') && blockadeFilter.some((e) => e === 'Ręczna'))
        setBlockadeFilterType(BlockadeTypeId.Any);
    } else setBlockadeFilterType(BlockadeTypeId.None);
  }, [blockadeFilter]);

  const handleBlockadeFilterChange = useCallback(
    (value?: string) => {
      if (assessmentType === assessmentTypes.sampleAssassment) return;
      
      let newBlockadeFilter: string[];
      if (value) {
        newBlockadeFilter = blockadeFilter.some((f) => f === value)
          ? blockadeFilter.filter((f) => f !== value)
          : [...blockadeFilter, value];
      } else {
        newBlockadeFilter = [];
      }
      
      dispatch(filtersActions.updateFilter({
        filterType: DataInfoType.ExaminersStructureInfo,
        newValues: {
          blockadeFilter: newBlockadeFilter,
        },
      }));
    },
    [blockadeFilter, dispatch, assessmentType],
  );

  const handleSearchPhraseChange = (value: string) => {
    dispatch(filtersActions.updateFilter({
      filterType: dataInfoType,
      newValues: {
        searchPhrase: value,
      },
    }));
  };

  const getBlockadeFilterMenu = () => {
    return (
      <>
        <TooltipOne title="Ustaw filtr do wyszukiwania: Blokada" placement="top">
          <FilterListIcon
            onClick={handleClickBlockadeMenu}
            aria-controls="ExamCommittee-menu"
            aria-haspopup="true"
            style={{
              cursor: 'pointer',
              color: blockadeFilter.length ? '#3560FF' : '',
              ...(isMinimized ? {} : { padding: '2px 0px 0px 5px' }),
            }}
          />
        </TooltipOne>
        <DropdownMenuFilter
          anchorEl={anchorElBlockade}
          setAnchorEl={setAnchorElBlockade}
          menuItems={['Automatyczna', 'Ręczna']}
          hasCheckbox={true}
          checkedItems={blockadeFilter}
          onChange={handleBlockadeFilterChange}
          uncheckAllMenuItems={handleBlockadeFilterChange}
        />
      </>
    );
  };

  if (!isSessionLoaded) {
    return null;
  }

  return (
    <div className={!isMinimized ? classes.usersList : classes.usersListMinimized}>
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            flexGrow: 1,
          }}
        >
          <div className={classes.usersListSearch}>
            <div className={classes.usersListHeader}>
              <TooltipOne title={isMinimized ? 'Struktura ZE' : ''}>
                <AccountTreeOutlinedIcon fontSize={'small'} onClick={handleStructureClick} />
              </TooltipOne>

              {!isMinimized && (
                <span className={classes.usersListTitle}>
                  {assessmentType === assessmentTypes.sampleAssassment ? 'Struktura ZWO' : 'Struktura ZE'}
                </span>
              )}
            </div>
          </div>
          {showBlockadeFilter && (
            <div className={classes.blockadeFilter}>
              {!isMinimized && 'Blokada'}
              {getBlockadeFilterMenu()}
            </div>
          )}

          {!isMinimized && isSearchOpen && (
            <div className={classes.usersListSearchInput}>
              <SearchIcon
                className={
                  highContrastMode
                    ? `${classes.usersListSearchIcon} ${classes.contrastColor}`
                    : classes.usersListSearchIcon
                }
                fontSize={'small'}
              />
              <InputBase
                style={{
                  width: '90%',
                  fontSize: '12px',
                  lineHeight: '14px',
                  color: 'rgba(44, 45, 62, 0.8)',
                  background: '#FFFFFF',
                  border: '1px solid rgba(44, 45, 62, 0.3)',
                  borderRadius: '5px',
                  paddingLeft: '24px',
                }}
                inputProps={{ 'aria-label': 'Szukaj' }}
                value={searchPhrase}
                onChange={(e) => handleSearchPhraseChange(e.target.value)}
              />
            </div>
          )}
        </div>
        {!isMinimized && (
          <div
            className={classes.usersListContainer}
            style={{
              height: '100%',
            }}
          >
            {displayedStructure &&
            user &&
            isAssessmentSessionLoaded &&
            (userAssessmentSessionInfo.function !== undefined ||
              userAssessmentSessionInfo.sampleAssessmentGroupFunction !== undefined) ? (
              <ExaminersTree
                structure={displayedStructure}
                search={searchFilter}
                isFirst={true}
                onActiveElementChange={handleActiveElementChange}
                onClosedElementChange={handleClosedElementChange}
                onReload={onReload}
                isControlProcessActive={assessmentSession?.isControlProcessActive ?? false}
                assessmentSession={assessmentSession}
                blockadeFilterType={blockadeFilterType}
              />
            ) : (
              !isEmptySearch && <CircularProgress size={20} />
            )}
            {isEmptySearch && (
              <div className={classes.emptySearchInfo}>
                Nie znaleziono rekordów spełniających zadane kryteria wyszukiwania.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SidebarList);
